import { useState } from 'react';
import { Trash2, FileText, Download, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { isImage, isPdf } from './fileValidation';
import { ImagePreviewModal } from './ImagePreviewModal';
import type { ImageDto } from './types';

export interface ImageGalleryProps {
  images: ImageDto[];
  onDelete?: (id: number) => void;
  canDelete?: boolean;
  compact?: boolean;
}

/**
 * Reusable image gallery component
 * - Grid layout with thumbnails
 * - PDF file support with icon
 * - Click to preview in modal
 * - Delete with confirmation
 */
export function ImageGallery({ images, onDelete, canDelete = false, compact = false }: ImageGalleryProps) {
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [previewImageId, setPreviewImageId] = useState<number | null>(null);

  const handleDeleteClick = (id: number) => {
    setDeleteConfirmId(id);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmId && onDelete) {
      onDelete(deleteConfirmId);
      setDeleteConfirmId(null);
    }
  };

  const handleView = (image: ImageDto) => {
    if (image.mimeType?.startsWith('image/')) {
      // For images, open preview modal
      setPreviewImageId(image.id);
    } else {
      // For PDFs and other non-image files, open in new tab
      window.open(image.filePath, '_blank');
    }
  };

  const handleDownload = (image: ImageDto) => {
    const link = document.createElement('a');
    link.href = image.filePath;
    link.download = image.originalFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (images.length === 0) {
    return (
      <div className={`rounded-lg border border-dashed border-gray-300 bg-gray-50 text-center ${compact ? 'p-3' : 'p-8'}`}>
        <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-500`}>No images uploaded</p>
      </div>
    );
  }

  const previewImageIndex = images.findIndex((img) => img.id === previewImageId);

  return (
    <>
      <div className={compact ? "grid grid-cols-4 gap-1" : "grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4"}>
        {images.map((image) => {
          const isImageFile = isImage(image.mimeType);
          const isPdfFile = isPdf(image.mimeType);

          return (
            <div
              key={image.id}
              className={`group relative aspect-square overflow-hidden border border-gray-200 bg-gray-100 ${compact ? 'rounded' : 'rounded-lg'}`}
            >
              {/* Image or PDF thumbnail */}
              <div
                onClick={() => handleView(image)}
                className="flex h-full w-full cursor-pointer items-center justify-center"
              >
                {isImageFile ? (
                  <img
                    src={image.filePath}
                    alt={image.originalFileName}
                    className="h-full w-full object-cover transition-transform group-hover:scale-105"
                  />
                ) : isPdfFile ? (
                  <div className={`flex flex-col items-center ${compact ? 'gap-0.5' : 'gap-2'}`}>
                    <FileText className={`${compact ? 'h-6 w-6' : 'h-12 w-12'} text-gray-400`} />
                    {!compact && (
                      <p className="px-2 text-center text-xs text-gray-600">
                        {image.originalFileName}
                      </p>
                    )}
                  </div>
                ) : null}
              </div>

              {/* Overlay with actions */}
              <div className={`absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100 ${compact ? 'gap-1' : 'gap-2'}`}>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleView(image);
                  }}
                  className={compact ? "h-5 w-5 p-0" : "h-8 w-8 p-0"}
                  title="View"
                >
                  <Eye className={compact ? "h-3 w-3" : "h-4 w-4"} />
                </Button>

                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(image);
                  }}
                  className={compact ? "h-5 w-5 p-0" : "h-8 w-8 p-0"}
                  title="Download"
                >
                  <Download className={compact ? "h-3 w-3" : "h-4 w-4"} />
                </Button>

                {canDelete && onDelete && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(image.id);
                    }}
                    className={compact ? "h-5 w-5 p-0" : "h-8 w-8 p-0"}
                    title="Delete"
                  >
                    <Trash2 className={compact ? "h-3 w-3" : "h-4 w-4"} />
                  </Button>
                )}
              </div>

              {/* File name label */}
              {!compact && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                  <p className="truncate text-xs text-white" title={image.originalFileName}>
                    {image.originalFileName}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={deleteConfirmId !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirmId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Image</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this image? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Image preview modal */}
      {previewImageIndex !== -1 && (
        <ImagePreviewModal
          images={images}
          initialIndex={previewImageIndex}
          onClose={() => setPreviewImageId(null)}
        />
      )}
    </>
  );
}
